import type { FC } from 'react';
import { IpcButton, IpcModal } from '../components/ui';
import {
  NODE_CONTEXT_FIELD_DOCS,
  RULE_SCRIPT_CONTEXT_KEYS,
} from '../types/nodeAutoCompleteRuleScriptContext';
import './NodeAutoCompletePrepareScriptHelpModal.css';

type Props = {
  open: boolean;
  onClose: () => void;
};

const EXAMPLE_SCRIPT = `function execute(context, services) {
  const triggerPayload = context.triggerPayload;
  const nodeContext = context.nodeContext;

  const rows = dbQuery(
    'SELECT id, status FROM ipc_schedule_milestones_tbl WHERE id = @id',
    { id: nodeContext.scheduleMilestoneId }
  );
  console.log('trigger', triggerPayload, 'skus', nodeContext.skus, 'rows', rows);
  return { rows };
}`;

const NodeAutoCompletePrepareScriptHelpModal: FC<Props> = ({ open, onClose }) => (
  <IpcModal
    open={open}
    title="数据准备脚本说明"
    width={760}
    onCancel={onClose}
    footer={(
      <div className="ipc-modal__footer-actions">
        <IpcButton variant="primary" onClick={onClose}>关闭</IpcButton>
      </div>
    )}
    className="acr-script-help-modal"
  >
    <div className="acr-script-help">
      <section className="acr-script-help__section">
        <h4 className="acr-script-help__heading">脚本约定</h4>
        <p className="acr-script-help__text">
          请编写入口函数 <code>execute(context, services)</code>。引擎使用 Jint 执行 JavaScript，
          本步骤返回值会写入动作决策步骤的 <code>context.preparedData</code>。
        </p>
        <pre className="acr-script-help__code">{EXAMPLE_SCRIPT}</pre>
      </section>

      <section className="acr-script-help__section">
        <h4 className="acr-script-help__heading">context 入参（第 2 步）</h4>
        <p className="acr-script-help__text">
          数据准备步骤的 <code>context</code> 固定包含以下两个字段：
        </p>
        <dl className="acr-script-help__dl">
          <div>
            <dt><code>context.{RULE_SCRIPT_CONTEXT_KEYS.triggerPayload}</code></dt>
            <dd>
              触发入参。接口正式触发为
              <code>{'{'} trigger_key, content {'}'}</code>
              ；MQ 正式触发为
              <code>{'{'} vhost, queue, content {'}'}</code>
              （content 分别为请求 Body / 消息体）。
            </dd>
          </div>
          <div>
            <dt><code>context.{RULE_SCRIPT_CONTEXT_KEYS.nodeContext}</code></dt>
            <dd>当前待执行排期节点的运行时数据，常见字段如下：</dd>
          </div>
        </dl>
        <ul className="acr-script-help__field-list">
          {NODE_CONTEXT_FIELD_DOCS.map(item => (
            <li key={item.field}>
              <code>{item.field}</code>
              <span>{item.description}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="acr-script-help__section">
        <h4 className="acr-script-help__heading">services 参数</h4>
        <dl className="acr-script-help__dl">
          <div>
            <dt><code>services</code></dt>
            <dd>内置服务对象，当前提供 <code>services.http</code> 用于调用外部 HTTP 接口。</dd>
          </div>
        </dl>
      </section>

      <section className="acr-script-help__section">
        <h4 className="acr-script-help__heading">已注入全局函数</h4>
        <div className="acr-script-help__fn">
          <div className="acr-script-help__fn-sig">
            <code>dbQuery(sql, parameters?)</code>
          </div>
          <p>只读数据库查询，仅允许 <code>SELECT</code> 语句。参数使用 <code>@name</code> 占位，例如 <code>{'{ id: 1 }'}</code>。</p>
          <p>返回行数组，每行是一个字段字典。</p>
        </div>
        <div className="acr-script-help__fn">
          <div className="acr-script-help__fn-sig">
            <code>completeScheduleMilestone(scheduleMilestoneId, actualStartDate, actualEndDate)</code>
          </div>
          <p>
            将排期单元格（<code>ipc_schedule_milestones_tbl.id</code>）标记为已完成。
            仅适用于配置为「自动完成」的里程碑节点。
          </p>
          <p>时间支持字符串（如 <code>2026-08-01 09:00:00</code>）或日期对象。</p>
        </div>
        <div className="acr-script-help__fn">
          <div className="acr-script-help__fn-sig">
            <code>completeScheduleSkuMilestone(projectId, scheduleId, specScheduleId, skuId, milestoneId, actualStartDate, actualEndDate)</code>
          </div>
          <p>
            完成 SKU 级节点：写入/更新 <code>ipc_schedule_sku_milestones_tbl</code>（稀疏覆盖），
            <strong>不修改</strong>规格级 <code>ipc_schedule_milestones_tbl</code>。
          </p>
          <p>
            <code>skuId</code> 为 IPD SKU 平铺 ID（<code>ipc_ipd_skus_tbl.id</code>）；
            须已通过规格关联绑定到该 <code>specScheduleId</code>。
          </p>
          <p>仅适用于「自动完成」节点。<code>actualStartDate</code> 允许为 <code>null</code>/空；<code>actualEndDate</code> 必填。</p>
        </div>
        <div className="acr-script-help__fn">
          <div className="acr-script-help__fn-sig">
            <code>completeSpecMilestoneIfAllSkusCompleted(projectId, scheduleId, specScheduleId, milestoneId)</code>
          </div>
          <p>
            判断该规格下全部关联 SKU 在该节点是否都已完成；若是，则将<strong>规格级</strong>节点标记为已完成。
          </p>
          <p>
            规格实际结束时间 = 各 SKU 实际结束时间的最大值；
            若规格实际开始为空，按顺序取值：计划开始 → 各 SKU 实际开始的最早值 → 规格实际结束。
          </p>
          <p>未全部完成时不会改规格节点，返回 <code>all_skus_completed: false</code>。</p>
        </div>
        <div className="acr-script-help__fn">
          <div className="acr-script-help__fn-sig">
            <code>console.log(...args)</code>
          </div>
          <p>输出调试日志，测试与执行记录中会保留日志内容。</p>
        </div>
      </section>

      <section className="acr-script-help__section">
        <h4 className="acr-script-help__heading">services.http</h4>
        <p className="acr-script-help__text">
          支持 <code>get</code>、<code>post</code>、<code>put</code>、<code>patch</code>、<code>delete</code>。
          第三个参数 <code>options</code> 可选：<code>headers</code>、<code>timeoutSec</code>、<code>contentType</code>。
        </p>
        <pre className="acr-script-help__code">{`const res = services.http.post('https://api.example.com/check', { id: nodeContext.scheduleMilestoneId }, {
  headers: { Authorization: 'Bearer token' },
  timeoutSec: 30
});
// res: { status, ok, headers, text, body }`}</pre>
      </section>

      <section className="acr-script-help__section">
        <h4 className="acr-script-help__heading">返回值</h4>
        <p className="acr-script-help__text">
          可返回任意 JSON 可序列化对象；<code>null</code> 表示无附加数据。
          动作决策步骤通过 <code>context.preparedData</code> 读取本步骤结果。
        </p>
      </section>
    </div>
  </IpcModal>
);

export default NodeAutoCompletePrepareScriptHelpModal;
